import React, { useEffect, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InstallIcon } from "components/Icons";
import { DataContext } from "providers/DataProvider";

/**
 * PWAInstallPrompt.jsx – ES module
 * ---------------------------------------------------------------------------
 * A self‑contained React component that offers a platform‑aware PWA install
 * experience without external UI libraries or CSS frameworks.
 *
 *   • Vibrating icon attracts first‑time users: **animation stops permanently
 *     after the very first click**, tracked with `localStorage` (key:
 *     "pwa-install-button-clicked"). No other state influences the animation.
 *   • Handles `beforeinstallprompt`, tailored fallback instructions, iOS/Android
 *     quirks, and hides itself when already running as an installed PWA.
 *
 * June 2025 change
 * ─────────────────
 * Replaced `installImageSrc` / `fallbackImageSrc` string props with two **SVG
 * React components**: `DefaultIcon` (animated until first click) and
 * `FallbackIcon` (static after dismissal). The component now wraps the chosen
 * SVG in a `motion.div` for animation.
 *
 * Props
 * ─────
 * • `DefaultIcon`   – React component that renders the SVG for the *active*
 *                     (not‑yet‑clicked) state.
 * • `FallbackIcon`  – React component that renders the SVG shown after the
 *                     user dismisses or rejects installation.
 */

export default function PWAInstallPrompt({
  DefaultIcon = InstallIcon,
  FallbackIcon = InstallIcon,
}) {
  // ─────────────────────────── State ───────────────────────────
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isPromptAvailable, setIsPromptAvailable] = useState(false);
  const [hasRejected, setHasRejected] = useState(
    () => localStorage.getItem("pwa-install-rejected") === "1"
  );
  const [buttonClicked, setButtonClicked] = useState(
    () => localStorage.getItem("pwa-install-button-clicked") === "1"
  );
  const { isInStandaloneMode } = useContext(DataContext);
  const [env, setEnv] = useState({ os: "Unknown", browser: "Unknown" });
  const [dialogState, setDialogState] = useState(null); // null | "prompt" | "fallback"

  // Capture beforeinstallprompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsPromptAvailable(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Detect platform and browser
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();

    const os = /iphone|ipad|ipod/.test(ua)
      ? "iOS"
      : /android/.test(ua)
      ? "Android"
      : /win/.test(ua)
      ? "Windows"
      : /mac/.test(ua)
      ? "macOS"
      : "Other";

    const browser = /edg\//.test(ua)
      ? "Edge"
      : /chrome|crios|chromium/.test(ua) && !/edg\//.test(ua)
      ? "Chrome"
      : /safari/.test(ua) && !/chrome|crios/.test(ua)
      ? "Safari"
      : /firefox/.test(ua)
      ? "Firefox"
      : "Other";

    setEnv({ os, browser });
  }, []);

  // ────────────────────────── Handlers ──────────────────────────
  const handleClick = () => {
    // Record first interaction (stops animation forever)
    if (!buttonClicked) {
      localStorage.setItem("pwa-install-button-clicked", "1");
      setButtonClicked(true);
    }

    if (hasRejected) return; // Don't annoy the user twice

    if (isPromptAvailable && deferredPrompt) {
      setDialogState("prompt");
    } else {
      setDialogState("fallback");
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome !== "accepted") {
      localStorage.setItem("pwa-install-rejected", "1");
      setHasRejected(true);
    }

    setDeferredPrompt(null);
    setDialogState(null);
  };

  const handleLater = () => {
    localStorage.setItem("pwa-install-rejected", "1");
    setHasRejected(true);
    setDialogState(null);
  };

  const closeDialog = () => setDialogState(null);

  // ────────────────────── UI helpers ──────────────────────
  const instructions = getInstallInstructions(env);
  const shouldAnimate = !buttonClicked; // ← sole determinant for animation

  // ─────────────────────────── Render ───────────────────────────

  if (isInStandaloneMode) return null; // Hide inside installed PWA

  const WrapperProps = {
    onClick: handleClick,
    role: "button",
    "aria-label": "Install app",
    style: {
      cursor: hasRejected ? "default" : "pointer",
      height: "100%",
      aspectRatio: "1/1",
      userSelect: "none",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  const ActiveIcon = buttonClicked ? FallbackIcon : DefaultIcon;

  return (
    <>
      {/* ───── Install button icon (vibrates until first click) ───── */}
      <motion.div
        {...WrapperProps}
        animate={
          shouldAnimate
            ? { rotate: [0, -10, 10, -10, 10, 0] }
            : {}
        }
        transition={
          shouldAnimate
            ? { duration: 0.36, repeat: Infinity, repeatDelay: 30, ease: "easeInOut" }
            : {}
        }
      >
        <ActiveIcon/>
      </motion.div>

      {/* ───────────────────────── Modal overlay ───────────────────────── */}
      <AnimatePresence>
        {dialogState && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={overlayStyle}
          >
            <motion.div
              initial={{ translateY: 40, opacity: 0 }}
              animate={{ translateY: 0, opacity: 1 }}
              exit={{ translateY: 40, opacity: 0 }}
              style={modalStyle}
            >
              {dialogState === "prompt" ? (
                <PromptModal onAccept={handleInstall} onLater={handleLater} />
              ) : (
                <FallbackModal instructions={instructions} onClose={closeDialog} />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ────────────────────── Sub‑components ──────────────────────
function PromptModal({ onAccept, onLater }) {
  return (
    <>
      <h2 style={titleStyle}>Install this app?</h2>
      <p>You can launch it from your home screen and even use it offline.</p>
      <div style={actionRowStyle}>
        <button style={secondaryBtnStyle} onClick={onLater}>
          Maybe later
        </button>
        <button style={primaryBtnStyle} onClick={onAccept}>
          Yes
        </button>
      </div>
    </>
  );
}

function FallbackModal({ instructions, onClose }) {
  return (
    <>
      <h2 style={titleStyle}>Install this app</h2>
      <p>Enjoy faster access and offline support by adding it to your device.</p>
      <p style={{ fontWeight: "bold", marginTop: 20 }}>{instructions.title}</p>
      <ol style={{ paddingInlineStart: 20 }}>
        {instructions.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <div style={{ textAlign: "right", marginTop: 24 }}>
        <button style={primaryBtnStyle} onClick={onClose}>
          Got it
        </button>
      </div>
    </>
  );
}

// ────────────────────── Styles (inline) ──────────────────────
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const modalStyle = {
  background: "#fff",
  borderRadius: 8,
  padding: 24,
  maxWidth: 420,
  width: "90%",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  color: '#444'
};

const titleStyle = { margin: 0, marginBottom: 12 };

const actionRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 24,
};

const baseBtn = {
  padding: "8px 16px",
  fontSize: 14,
  borderRadius: 4,
  cursor: "pointer",
  border: "none",
};

const primaryBtnStyle = {
  ...baseBtn,
  background: "#2e7d32",
  color: "#fff",
};

const secondaryBtnStyle = {
  ...baseBtn,
  background: "#e0e0e0",
};

// ────────────────────── Helper: install instructions ──────────────────────
function getInstallInstructions({ os, browser }) {
  if (os === "iOS" && browser === "Safari") {
    return {
      title: "iOS · Safari",
      steps: [
        "Tap the share icon at the bottom.",
        "Scroll down and tap ‘Add to Home Screen’.",
        "Tap ‘Add’ in the top‑right.",
      ],
    };
  }

  if (os === "Android" && browser === "Chrome") {
    return {
      title: "Android · Chrome",
      steps: [
        "Tap the ⋮ menu in the top‑right.",
        "Choose ‘Add to Home screen’.",
        "Confirm by tapping ‘Add’.",
      ],
    };
  }

  if (os === "Windows" && browser === "Edge") {
    return {
      title: "Windows · Edge",
      steps: [
        "Click the ‘App available’ icon in the address bar.",
        "Click ‘Install’.",
      ],
    };
  }

  return {
    title: `${browser} on ${os}`,
    steps: ["Look for ‘Install’ or ‘Add to Home screen’ in your browser menu."],
  };
}